import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  createTenant,
  fetchTenants,
  finalizeTenantCancellation,
  updateTenant,
  updateTenantPaymentStatus,
  withdrawTenantCancellation,
  type AdminTenant,
  type TenantListFilter,
} from "@/api/admin";
import { apiErrorMessage } from "@/api/client";
import type { Pagination, PaymentStatus, Plan } from "@/api/types";
import { formatDateTime } from "@/utils/format";

const PAYMENT_BADGE: Record<PaymentStatus, { label: string; cls: string }> = {
  active: { label: "正常", cls: "badge-green" },
  past_due: { label: "支払い失敗", cls: "badge-red" },
  cancelled: { label: "解約済み", cls: "badge-gray" },
};

const PLAN_OPTIONS: { value: Plan; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "standard", label: "Standard" },
  { value: "premium", label: "Premium" },
];

export function AdminTenantsPage() {
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1, per_page: 20, total: 0, total_pages: 0,
  });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<TenantListFilter>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<AdminTenant | null>(null);
  const [cancellationBusyId, setCancellationBusyId] = useState<number | null>(null);

  const finalizeCancellation = async (t: AdminTenant) => {
    const confirmMsg =
      `「${t.name}」の退会を確定します。\n\n` +
      `この操作は元に戻せません。\n` +
      `・サブスクリプションは解約扱いになります\n` +
      `・アカウントは停止され、ログインできなくなります\n` +
      `・未処理の発注がある場合は、先に処理してから実行してください`;
    if (!window.confirm(confirmMsg)) return;
    setCancellationBusyId(t.id);
    setFlash(null);
    try {
      await finalizeTenantCancellation(t.id);
      setFlash({ type: "ok", text: `「${t.name}」の退会を確定しました` });
      await load();
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "退会確定に失敗しました") });
    } finally {
      setCancellationBusyId(null);
    }
  };

  const withdrawCancellation = async (t: AdminTenant) => {
    if (!window.confirm(`「${t.name}」の退会申請を取り消しますか？`)) return;
    setCancellationBusyId(t.id);
    setFlash(null);
    try {
      await withdrawTenantCancellation(t.id);
      setFlash({ type: "ok", text: `「${t.name}」の退会申請を取り消しました` });
      await load();
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "取り消しに失敗しました") });
    } finally {
      setCancellationBusyId(null);
    }
  };
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const toggleActive = async (t: AdminTenant) => {
    const goingInactive = t.is_active;
    const msg = goingInactive
      ? `「${t.name}」のアカウントを停止しますか？\n\n停止中は:\n・新規ログインできません\n・既存のログインセッションでも API アクセスが拒否されます\n・未処理の発注は管理画面から個別に処理してください`
      : `「${t.name}」のアカウントを再開しますか？\n\n停止前と同じ状態で利用できるようになります。`;
    if (!window.confirm(msg)) return;

    setTogglingId(t.id);
    setFlash(null);
    try {
      await updateTenant(t.id, { is_active: !t.is_active });
      setFlash({
        type: "ok",
        text: goingInactive
          ? `「${t.name}」を停止しました`
          : `「${t.name}」を再開しました`,
      });
      await load();
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "状態の変更に失敗しました") });
    } finally {
      setTogglingId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchTenants(page, 20, filter);
      setTenants(res.tenants);
      setPagination(res.pagination);
    } catch (e) {
      setError(apiErrorMessage(e, "顧客一覧の取得に失敗しました"));
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">顧客管理</h1>
        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
          新規顧客登録
        </button>
      </div>

      <div className="flex items-center gap-3">
        <label className="label m-0" htmlFor="filter">表示</label>
        <select
          id="filter"
          className="input w-64"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value as TenantListFilter);
            setPage(1);
          }}
        >
          <option value="">すべて</option>
          <option value="requested">退会申請中</option>
          <option value="finalized">退会確定済み</option>
        </select>
      </div>

      {flash && (
        <div
          className={`rounded-md px-4 py-3 text-sm border ${
            flash.type === "ok"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {flash.text}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">読み込み中...</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-left font-medium">ID</th>
                <th className="px-3 py-2 text-left font-medium">企業名</th>
                <th className="px-3 py-2 text-left font-medium">担当者 / メール</th>
                <th className="px-3 py-2 text-left font-medium">プラン</th>
                <th className="px-3 py-2 text-center font-medium">状態</th>
                <th className="px-3 py-2 text-center font-medium">支払い</th>
                <th className="px-3 py-2 text-right font-medium">ユーザ数</th>
                <th className="px-3 py-2 text-left font-medium">登録日</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tenants.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">{t.id}</td>
                  <td className="px-3 py-2 font-medium">{t.name}</td>
                  <td className="px-3 py-2">
                    <div>{t.contact_name || "—"}</div>
                    {t.user_emails.length > 0 && (
                      <div className="text-xs text-gray-500">
                        {t.user_emails[0]}
                        {t.user_emails.length > 1 && (
                          <span
                            className="ml-1 text-gray-400"
                            title={t.user_emails.slice(1).join(", ")}
                          >
                            他 {t.user_emails.length - 1} 件
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 uppercase">{t.plan}</td>
                  <td className="px-3 py-2 text-center">
                    {t.is_active ? (
                      <span className="badge-green">有効</span>
                    ) : (
                      <span className="badge-red">停止</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={PAYMENT_BADGE[t.payment_status].cls}>
                      {PAYMENT_BADGE[t.payment_status].label}
                    </span>
                    {t.cancellation_requested_at && !t.cancellation_finalized_at && (
                      <div className="mt-1">
                        <span className="badge-yellow">退会申請中</span>
                      </div>
                    )}
                    {t.cancellation_finalized_at && (
                      <div className="mt-1">
                        <span className="badge-gray">退会確定済み</span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{t.user_count}</td>
                  <td className="px-3 py-2 text-gray-600">{formatDateTime(t.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex gap-2 justify-end items-center flex-wrap">
                      <button
                        type="button"
                        className="text-brand-700 hover:underline"
                        onClick={() => setEditing(t)}
                        disabled={togglingId === t.id || cancellationBusyId === t.id}
                      >
                        編集
                      </button>
                      {t.cancellation_requested_at && !t.cancellation_finalized_at && (
                        <>
                          <button
                            type="button"
                            className="btn-danger px-2 py-1 text-xs"
                            onClick={() => void finalizeCancellation(t)}
                            disabled={cancellationBusyId === t.id}
                            title="退会を確定してアカウントを停止"
                          >
                            {cancellationBusyId === t.id ? "処理中..." : "退会確定"}
                          </button>
                          <button
                            type="button"
                            className="btn-secondary px-2 py-1 text-xs"
                            onClick={() => void withdrawCancellation(t)}
                            disabled={cancellationBusyId === t.id}
                            title="退会申請を取り消す（引き留め）"
                          >
                            {cancellationBusyId === t.id ? "処理中..." : "申請取消"}
                          </button>
                        </>
                      )}
                      {!t.cancellation_requested_at && !t.cancellation_finalized_at && (
                        t.is_active ? (
                          <button
                            type="button"
                            className="btn-danger px-2 py-1 text-xs"
                            onClick={() => void toggleActive(t)}
                            disabled={togglingId === t.id}
                            title="このアカウントを停止"
                          >
                            {togglingId === t.id ? "処理中..." : "停止"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn-secondary px-2 py-1 text-xs"
                            onClick={() => void toggleActive(t)}
                            disabled={togglingId === t.id}
                            title="停止中のアカウントを再開"
                          >
                            {togglingId === t.id ? "処理中..." : "再開"}
                          </button>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            前へ
          </button>
          <div className="text-sm">
            {pagination.page} / {pagination.total_pages}（{pagination.total} 件）
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            disabled={page >= pagination.total_pages}
          >
            次へ
          </button>
        </div>
      )}

      {showCreate && (
        <CreateTenantDialog
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            setFlash({ type: "ok", text: "顧客を登録しました" });
            await load();
          }}
          onError={(msg) => setFlash({ type: "err", text: msg })}
        />
      )}

      {editing && (
        <EditTenantDialog
          tenant={editing}
          onClose={() => setEditing(null)}
          onUpdated={async () => {
            setEditing(null);
            setFlash({ type: "ok", text: "顧客情報を更新しました" });
            await load();
          }}
          onError={(msg) => setFlash({ type: "err", text: msg })}
        />
      )}
    </div>
  );
}

// ---------- Dialogs ----------

function DialogShell({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-full overflow-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold">{title}</h2>
          <button type="button" className="text-gray-500 hover:text-gray-700" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function CreateTenantDialog({
  onClose,
  onCreated,
  onError,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [name, setName] = useState("");
  const [plan, setPlan] = useState<Plan>("light");
  // 配送先は LOGEX 固定運用のためテナント別の住所/郵便番号入力は廃止
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createTenant({
        name: name.trim(),
        plan,
        initial_email: email.trim() || undefined,
        initial_password: password || undefined,
      });
      await onCreated();
    } catch (e) {
      onError(apiErrorMessage(e, "登録に失敗しました"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogShell title="新規顧客登録" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="label">企業名</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="label">プラン</label>
          <select
            className="input"
            value={plan}
            onChange={(e) => setPlan(e.target.value as Plan)}
          >
            {PLAN_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900">
          📦 配送先は <strong>株式会社 LOGEX 宛（固定）</strong>。テナント別の住所登録は不要です。
        </div>
        <div className="border-t pt-3">
          <p className="text-sm font-medium mb-2">初期ログインユーザー（任意）</p>
          <div className="space-y-2">
            <div>
              <label className="label">メール</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="label">初期パスワード（8文字以上）</label>
              <input
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            キャンセル
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "登録中..." : "登録"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

function EditTenantDialog({
  tenant,
  onClose,
  onUpdated,
  onError,
}: {
  tenant: AdminTenant;
  onClose: () => void;
  onUpdated: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [name, setName] = useState(tenant.name);
  const [plan, setPlan] = useState<Plan>(tenant.plan);
  const [isActive, setIsActive] = useState(tenant.is_active);
  // 配送先住所/受取人系はテナント別の入力を廃止（LOGEX 固定運用、env で管理）
  const [storeCode, setStoreCode] = useState(tenant.store_code ?? "kantan_JP");
  const [currency, setCurrency] = useState(tenant.currency ?? "JP");
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(tenant.payment_status);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await updateTenant(tenant.id, {
        name: name.trim(),
        plan,
        is_active: isActive,
        store_code: storeCode.trim() || undefined,
        currency: currency.trim() || undefined,
      });
      if (paymentStatus !== tenant.payment_status) {
        await updateTenantPaymentStatus(tenant.id, paymentStatus);
      }
      await onUpdated();
    } catch (e) {
      onError(apiErrorMessage(e, "更新に失敗しました"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogShell title={`顧客編集: ${tenant.name}`} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="label">企業名</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="label">プラン</label>
          <select
            className="input"
            value={plan}
            onChange={(e) => setPlan(e.target.value as Plan)}
          >
            {PLAN_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label flex items-center gap-2">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            アカウント有効
          </label>
          {!isActive && (
            <p className="text-xs text-red-600 mt-1">
              停止にするとこの顧客はログインできなくなります。
            </p>
          )}
        </div>
        <div>
          <label className="label">支払い状態</label>
          <select
            className="input"
            value={paymentStatus}
            onChange={(e) => setPaymentStatus(e.target.value as PaymentStatus)}
          >
            <option value="active">正常 (active) — 通常動作</option>
            <option value="past_due">支払い失敗 (past_due) — 新規発注ブロック＋警告バナー</option>
            <option value="cancelled">解約済み (cancelled) — 自動的に停止状態になる</option>
          </select>
          {paymentStatus === "past_due" && (
            <p className="text-xs text-red-700 mt-1">
              顧客のカタログ/カート画面に警告バナーが表示され、新規発注はブロックされます。
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            本番では Stripe Webhook で自動遷移します。ここは運用・デモ用の手動切替です。
          </p>
        </div>
        <div className="border-t pt-3">
          <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-900">
            <div className="font-semibold">配送先: 株式会社 LOGEX 宛（固定）</div>
            <div className="text-xs mt-1 text-blue-800">
              全テナントの発注は LOGEX 1 箇所で受取・転送する運用です。
              個別の発送先設定は不要のため UI からは非表示にしています
              （実値は <code>.env.prod</code> の LOGEX_* で設定）。
            </div>
          </div>
        </div>
        <div className="border-t pt-3">
          <p className="text-sm font-medium mb-2">
            Air-Logi 出庫指示 CSV 用設定
          </p>
          <p className="text-xs text-gray-500 mb-3">
            出庫指示 CSV 生成時にテナント別に使う値（受取人情報は LOGEX 固定）。
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">店舗コード (order_place)</label>
              <input
                className="input"
                value={storeCode}
                onChange={(e) => setStoreCode(e.target.value)}
                placeholder="kantan_JP"
              />
            </div>
            <div>
              <label className="label">通貨 (currency)</label>
              <input
                className="input"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                placeholder="JP"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            キャンセル
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "保存中..." : "保存"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}
